<template>
  <PrismicLink
    v-if="field && field.url"
    :field="field"
    class="button"
    :class="[
      type,
      {
        hasIcon: !!icon,
      },
    ]"
  >
    <span v-html="field.text || text"></span>
    <slot></slot>
    <span class="button-ico-wrap" v-if="icon">
      <BaseIcon
        :name="icon"
        @click="emit('rightIconClick')"
        class="button-ico right"
        :class="[iconClass, 'white']"
      />
    </span>
  </PrismicLink>
  <component
    v-else
    :is="tag"
    class="button"
    :class="[
      type,
      {
        hasIcon: !!icon,
      },
    ]"
    :disabled="disabled"
  >
    <span v-html="field?.text || text"></span>
    <slot></slot>
    <span class="button-ico-wrap" v-if="icon">
      <BaseIcon
        :name="icon"
        @click="emit('rightIconClick')"
        class="button-ico right"
        :class="[iconClass, 'white']"
      />
    </span>
  </component>
</template>

<script setup>
import { PrismicLink } from "@prismicio/vue";
const emit = defineEmits(["leftIconClick", "rightIconClick"]);
defineOptions({
  name: "BaseButton",
});

const props = defineProps({
  /**
   * @prop {String} type - The type of the button.
   *                        Default is "primary".
   *                        primary, secondary, bigCircle, close, icon
   */
  type: {
    type: String,
    default: "primary",
  },
  /**
   * @property {Object} field - prismic field for links.
   * @default null
   */
  field: {
    type: Object,
    default: null,
  },
  tag: {
    type: String,
    default: "button",
  },
  tagType: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
  iconClass: {
    type: String,
    default: "fill",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const tagComputed = computed(() => {
  if (props.field) return "prismic-link";
  return props.tag;
});
</script>

<style lang="scss" scoped>
.button {
  -webkit-tap-highlight-color: transparent;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  will-change: transform;
  border: 0;
  font-family: $main-font;
  height: toRem($button-height);
  min-height: toRem($button-height);
  outline: none;
  cursor: pointer;
  transition: $transition;
  text-decoration: none !important;
  color: $black;
  padding: toRem(8) toRem(33);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-style: normal;
  font-weight: 600;
  font-size: toRem(24);
  perspective: 1000px;
  // min-width: toRem(100);
  border-radius: toRem(6);
  z-index: 1;
  max-width: 100%;
  min-width: fit-content;
  @media (max-width: $tablet2) {
    font-size: toRem(20);
    font-weight: 500;
    height: toRem(56);
    min-height: toRem(56);
  }
  &.w260 {
    width: toRem(260);
  }
  &.w372 {
    width: toRem(372);
  }
  &.w400 {
    width: toRem(400);
  }
  &.w414 {
    width: toRem(414);
  }

  &.active {
    cursor: default;
  }

  &-ico-wrap {
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    transition: $transition;
  }
  &-ico {
    width: toRem(24);
    height: toRem(24);
    display: block;
    :deep(svg) {
      width: toRem(24);
      height: toRem(24);
      display: block;
    }
    @media (max-width: $tablet2) {
      width: toRem(18);
      height: toRem(18);
      :deep(svg) {
        width: toRem(18);
        height: toRem(18);
      }
    }
  }
  &.primary,
  &.secondary,
  &.icon {
    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      transition: $transition;
      z-index: -1;
      border: 2px solid transparent;
      border-right: 0;
      border-radius: toRem(6);
    }
  }
  &.primary,
  &.secondary {
    &.hasIcon {
      padding-right: toRem(33 + 85);
      &::before {
        right: toRem(84);
        border-radius: toRem(6) 0 0 toRem(6);
      }
    }

    .button-ico-wrap {
      width: toRem(84);
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0 toRem(6) toRem(6) 0;
      &::before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -2px;
        bottom: -2px;
        transition: $transition;
        border-radius: 0 toRem(6) toRem(6) 0;
        z-index: -1;
        border: 2px solid $white;
      }
    }

    @include myHover {
      &::before {
        background: $white;
        border-color: $white;
      }
      .button-ico-wrap {
        border-color: $white;
        background: $black;
      }
    }
    &[disabled],
    &.disabled {
      // opacity: 0.7;
    }
    @media (max-width: $tablet2) {
      &.hasIcon {
        padding-right: toRem(56 + 33);
        &::before {
          right: toRem(56);
        }
      }
      .button-ico-wrap {
        width: toRem(56);
      }
    }
  }

  &.primary {
    color: $black;
    .button-ico-wrap {
      background: $black;
      box-shadow: 0 0 toRem(40) 0 rgba(253, 96, 5, 0.3) inset;

      &::before {
        border-color: $orange;
      }
    }
    &::before {
      background: $orange;
      border: 2px solid $orange;
    }

    @include myHover {
      color: $black;
      .button-ico-wrap {
        box-shadow: 0 0 toRem(40) 0 rgba(253, 253, 253, 0.15) inset;
        &::before {
          border-color: $white;
        }
      }
    }
  }
  &.secondary {
    color: $white;
    &.hasIcon {
    }
    &::before {
      border: 2px solid $white;
      box-shadow: 0 0 toRem(40) 0 rgba(253, 253, 253, 0.15) inset;
    }
    .button-ico-wrap {
      box-shadow: 0 0 toRem(40) 0 rgba(253, 253, 253, 0.15) inset;
    }

    @include myHover {
      color: $black;
    }
  }

  &.icon {
    width: toRem(60);
    height: toRem(60);
    min-height: toRem(60);
    padding: 0;
    transition: 0s;
    .button-ico {
      width: toRem(16);
      height: toRem(16);
    }

    &::before {
      background: $black272727;
      border-radius: 50%;
      border: 1px solid $dark676767;
    }
    .button-ico-wrap {
      position: relative;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
    }
    &:active {
      &::before {
        transform: scale(1.05);
      }
    }
    @include myHover {
      color: $black;
      &::before {
        border-color: $white;
      }
    }
  }
  &.close {
    width: toRem(80);
    height: toRem(80);
    min-height: toRem(80);
    color: $black;
    padding: 0;
    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      transition: $transition;
      z-index: -1;
      border-radius: 50%;
      background: $white;
    }
    .button-ico-wrap {
      position: relative;
      left: 0;
      right: 0;
      text-align: center;
      margin: auto;
      display: inline-block;
    }
    :deep(.icon:not(.fill) path) {
      stroke: $black;
    }
    :deep(.icon.fill path) {
      fill: $black;
    }
    &:active {
      &::before {
        transform: scale(1.05);
      }
    }
    @include myHover {
      &::before {
        background: $black;
      }
      :deep(.icon:not(.fill) path) {
        stroke: $white;
      }
      :deep(.icon.fill path) {
        fill: $white;
      }
    }
    @media (max-width: $tablet2) {
      width: toRem(60);
      height: toRem(60);
      min-height: toRem(60);
    }
  }
  &.bigCircle {
    width: toRem(206);
    height: toRem(206);
    min-width: toRem(206);
    min-height: toRem(206);
    color: $white;
    flex-shrink: 0;
    flex-direction: column;
    //
    font-size: toRem(20);
    font-weight: 500;
    line-height: toRem(26);
    padding-left: toRem(20);
    padding-right: toRem(20);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      transition: $transition;
      z-index: -1;
      border-radius: 50%;
      box-shadow: 0px 0px 41.1px 0px rgba(253, 253, 253, 0.3) inset;
      background: $black;
      border: 2.5px solid $white;
    }

    .button-ico-wrap {
      position: relative;
      left: 0;
      right: 0;
      top: toRem(11);
      bottom: 0;
      @media (max-width: $mobile) {
        top: toRem(5);
      }
    }
    @include myHover {
      color: $black;
      &::before {
        background: $white;
      }
      :deep(.icon:not(.fill) path) {
        stroke: $black;
      }
      :deep(.icon.fill path) {
        fill: $black;
      }
    }
    &:active {
      &::before {
        transform: scale(1.05);
      }
    }

    @media (max-width: $tablet2) {
      width: toRem(156);
      height: toRem(156);
      min-width: toRem(156);
      min-height: toRem(156);
    }
  }

  &.navigation {
    width: toRem(80);
    height: toRem(80);
    min-height: toRem(80);
    background: $black;
    padding: 0;
    border: 2.5px solid $white;
    box-shadow: 0 0 toRem(41.1) 0 rgba(253, 253, 253, 0.2) inset;
    .button-ico-wrap {
      position: relative;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
    }

    &.prev {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &.next {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    @include myHover {
      background: $white;
      :deep(.icon:not(.fill) path) {
        stroke: $black;
      }
      :deep(.icon.fill path) {
        fill: $black;
      }
    }

    &[disabled],
    &.disabled {
      opacity: 0.3;
    }
  }

  &[disabled],
  &.disabled {
    // opacity: 0.7;
    cursor: default;
  }
}
.mobileButtonWidth {
  @media (max-width: $tablet) {
    min-width: toRem(414) !important;
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: $mobile) {
    min-width: 100% !important;
  }
}
</style>
